import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useFetchAllObjectQuery,
  useFetchAllViewQuery,
  useFetchDictionariesByCategoryCodeQuery,
} from '@redux/api'
import { useFetchAllSearchAssistantsQuery } from '@redux/api/searchAssistant.api'
import {
  useCreateOrUpdateTimeLineMutation,
  useLazyFetchTimeLineConfigByIdQuery,
} from '@redux/api/timeline.api'

import { usePrompt, useYupValidationResolver } from '@hooks'
import { CATEGORIES, ROUTES, TIMELINE_FORM_STEP_FIELDS } from '@constants'
import { AutocompleteOption, TimeLineGridConfig } from '@types'

import { timelineConfigurationSchema } from '@validations'

import {
  convertFormDataToRequestData,
  convertResponseDataToFormData,
  getDefaultValues,
} from '../helpers'

import { useGeneralHandlers } from './useGeneralHandlers'
import { useMainObjectHandlers } from './useMainObjectHandlers'
import { useObjectXHandlers } from './useObjectXHandlers'
import { useObjectYHandlers } from './useObjectYHandlers'

export const useHandlers = () => {
  const { id: timeLineId } = useParams()
  const navigate = useNavigate()

  const methods = useForm<TimeLineGridConfig>({
    defaultValues: getDefaultValues(),
    resolver: useYupValidationResolver(timelineConfigurationSchema),
    mode: 'all',
  })
  const {
    watch,
    clearErrors,
    formState: { isDirty },
    reset,
    trigger,
  } = methods

  const [fetchTimeLine, { isFetching: isTimeLineLoading }] = useLazyFetchTimeLineConfigByIdQuery()
  const [createOrUpdate, { isLoading: isCreateOrUpdateLoading }] =
    useCreateOrUpdateTimeLineMutation()
  const { data: objects } = useFetchAllObjectQuery()
  const { data: bindingValuesColumns } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_COLUMN
  )
  const { data: views } = useFetchAllViewQuery()
  const { data: searchAssistants } = useFetchAllSearchAssistantsQuery()

  const [activeStep, setActiveStep] = useState(0)

  const watchId = watch('id')

  const objectsOptions: AutocompleteOption[] = useMemo(
    () => objects?.map(obj => ({ id: obj.code, label: obj.title })) || [],
    [objects]
  )

  const objectYHandlers = useObjectYHandlers({
    methods,
    bindingValuesColumns,
    handleSaveTimeLine,
  })
  const objectXHandlers = useObjectXHandlers({ methods, bindingValuesColumns, handleSaveTimeLine })
  const generalHandlers = useGeneralHandlers({ methods, handleSaveTimeLine })
  const mainObjectHandlers = useMainObjectHandlers({ methods })

  const viewsOptions: AutocompleteOption[] = useMemo(() => {
    if (!views?.data.length) {
      return []
    }

    return views?.data.map(item => ({
      id: item.id,
      label: item.title,
      code: item.code,
    }))
  }, [views?.data])

  const searchAssistantsOptions: AutocompleteOption[] = useMemo(
    () => searchAssistants?.map(({ id, title: label }) => ({ id, label })) || [],
    [searchAssistants]
  )

  const globalDirty = isDirty

  usePrompt({ when: isDirty })

  useEffect(() => {
    if (timeLineId) {
      fetchTimeLine(timeLineId)
        .unwrap()
        .then(resp => {
          if (resp) {
            const data = convertResponseDataToFormData(resp)

            reset(data)

            return
          }

          handleCancelTimeLine()
        })
    }
  }, [timeLineId])

  function handleSaveTimeLine() {
    const requestData = convertFormDataToRequestData(methods.getValues())

    clearErrors()

    return createOrUpdate(requestData)
      .unwrap()
      .then(resp => {
        if (resp) {
          const data = convertResponseDataToFormData(resp)

          reset({
            ...data,
          })
        }
      })
  }

  const handleChangeActiveStep = (index: number) => {
    for (let i = 0; i < index; i++) {
      TIMELINE_FORM_STEP_FIELDS[i].map(item => trigger(item))
    }

    setActiveStep(index)
  }

  const handleCancelTimeLine = () => {
    navigate(ROUTES.TIME_LINE_GRID)
  }

  return {
    state: {
      methods,
      isTimeLineLoading,
      isCreateOrUpdateLoading,
      globalDirty,
      watchId,
      activeStep,
      ...objectYHandlers.state,
      ...objectXHandlers.state,
      ...generalHandlers.state,
      ...mainObjectHandlers.state,
    },
    data: {
      objectsOptions,
      viewsOptions,
      searchAssistantsOptions,
      ...objectYHandlers.data,
      ...objectXHandlers.data,
      ...generalHandlers.data,
      ...mainObjectHandlers.data,
      timeLineId,
    },
    handlers: {
      handleSaveTimeLine,
      handleCancelTimeLine,
      handleChangeActiveStep,
      ...objectYHandlers.handlers,
      ...objectXHandlers.handlers,
      ...generalHandlers.handlers,
      ...mainObjectHandlers.handlers,
    },
  }
}
